/**
 * Word budgets for essays, papers and dissertations.
 *
 * A limit is split across sections by percentage with the largest-remainder method, so
 * the section counts are whole numbers that add up to the limit exactly (when the
 * percentages add up to 100). Paragraph counts are either fixed by the structure — a
 * five-paragraph essay has one per section — or estimated from a target paragraph
 * length, and sentences per paragraph from an average sentence length. Both lengths are
 * assumptions the reader can change, and the widget says which ones it used.
 */

export const BUDGET_PRESETS = [
  "fiveParagraph",
  "argumentative",
  "compareContrast",
  "imrad",
  "literatureReview",
  "dissertation",
] as const;

export type BudgetPreset = (typeof BUDGET_PRESETS)[number];

interface PresetShape {
  /** Default share of the limit per section, in percent; each list sums to 100. */
  percents: readonly number[];
  /** One paragraph per section, whatever the length (the five-paragraph essay). */
  oneParagraphEach: boolean;
}

export const PRESET_SHAPES: Record<BudgetPreset, PresetShape> = {
  fiveParagraph: { percents: [15, 25, 25, 25, 10], oneParagraphEach: true },
  argumentative: {
    percents: [10, 20, 20, 20, 20, 10],
    oneParagraphEach: false,
  },
  compareContrast: {
    percents: [10, 25, 25, 25, 15],
    oneParagraphEach: false,
  },
  imrad: { percents: [15, 20, 25, 30, 10], oneParagraphEach: false },
  literatureReview: {
    percents: [10, 25, 25, 25, 15],
    oneParagraphEach: false,
  },
  dissertation: {
    percents: [10, 20, 15, 20, 25, 10],
    oneParagraphEach: false,
  },
};

/** English section names per preset, in order. */
export const PRESET_SECTIONS: Record<BudgetPreset, readonly string[]> = {
  fiveParagraph: [
    "Introduction",
    "Body paragraph 1",
    "Body paragraph 2",
    "Body paragraph 3",
    "Conclusion",
  ],
  argumentative: [
    "Introduction",
    "First reason",
    "Second reason",
    "Third reason",
    "Counterargument and rebuttal",
    "Conclusion",
  ],
  compareContrast: [
    "Introduction",
    "Point of comparison 1",
    "Point of comparison 2",
    "Point of comparison 3",
    "Conclusion",
  ],
  imrad: ["Introduction", "Methods", "Results", "Discussion", "Conclusion"],
  literatureReview: [
    "Introduction",
    "Theme 1",
    "Theme 2",
    "Theme 3",
    "Gaps and conclusion",
  ],
  dissertation: [
    "Introduction",
    "Literature review",
    "Methodology",
    "Findings",
    "Discussion",
    "Conclusion",
  ],
};

/** Words per page at 12-point Times New Roman, 1-inch margins, US Letter. */
export const WORDS_PER_PAGE = {
  double: 250,
  oneAndHalf: 333,
  single: 500,
} as const;

export const DEFAULT_PARAGRAPH_WORDS = 150;
export const DEFAULT_SENTENCE_WORDS = 20;
/** Past this, a paragraph is hard to follow and worth splitting. */
export const LONG_PARAGRAPH_WORDS = 250;

/**
 * Split `total` by `percents` into whole words that add up to the rounded total share,
 * giving the leftover words to the sections with the largest fractional remainders.
 */
export function allocateWords(
  total: number,
  percents: readonly number[]
): number[] {
  const raw = percents.map((percent) => (total * Math.max(0, percent)) / 100);
  const result = raw.map((value) => Math.floor(value));
  const target = Math.round(raw.reduce((sum, value) => sum + value, 0));
  let remaining = target - result.reduce((sum, value) => sum + value, 0);
  const order = raw
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
  for (const { index } of order) {
    if (remaining <= 0) {
      break;
    }
    result[index] += 1;
    remaining -= 1;
  }
  return result;
}

/**
 * Rescale shares so they add up to exactly 100, to one decimal place — the same
 * largest-remainder rounding as the word split, in tenths of a percent.
 */
export function scaleTo100(percents: readonly number[]): number[] {
  const clean = percents.map((percent) => Math.max(0, percent));
  if (clean.length === 0) {
    return [];
  }
  const sum = clean.reduce((total, percent) => total + percent, 0);
  if (sum === 0) {
    return scaleTo100(clean.map(() => 1));
  }
  const shares = clean.map((percent) => (percent * 100) / sum);
  return allocateWords(1000, shares).map((tenths) => tenths / 10);
}

export function pagesToWords(pages: number, wordsPerPage: number): number {
  return Math.round(Math.max(0, pages) * Math.max(0, wordsPerPage));
}

export interface BudgetSettings {
  oneParagraphEach: boolean;
  paragraphWords: number;
  sentenceWords: number;
}

export interface BudgetRow {
  percent: number;
  words: number;
  paragraphs: number;
  wordsPerParagraph: number;
  sentencesPerParagraph: number;
  /** Paragraphs this long are hard to follow. */
  long: boolean;
}

export function paragraphsFor(words: number, settings: BudgetSettings): number {
  if (settings.oneParagraphEach) {
    return 1;
  }
  return Math.max(1, Math.round(words / Math.max(1, settings.paragraphWords)));
}

export function buildBudget(
  total: number,
  percents: readonly number[],
  settings: BudgetSettings
): BudgetRow[] {
  const words = allocateWords(total, percents);
  return percents.map((percent, index) => {
    const sectionWords = words[index] ?? 0;
    const paragraphs = paragraphsFor(sectionWords, settings);
    const wordsPerParagraph = Math.round(sectionWords / paragraphs);
    return {
      percent,
      words: sectionWords,
      paragraphs,
      wordsPerParagraph,
      sentencesPerParagraph: Math.max(
        1,
        Math.round(wordsPerParagraph / Math.max(1, settings.sentenceWords))
      ),
      long: wordsPerParagraph > LONG_PARAGRAPH_WORDS,
    };
  });
}

export interface BudgetTotals {
  percent: number;
  words: number;
  paragraphs: number;
}

export function budgetTotals(rows: readonly BudgetRow[]): BudgetTotals {
  return {
    percent:
      Math.round(rows.reduce((sum, row) => sum + row.percent, 0) * 10) / 10,
    words: rows.reduce((sum, row) => sum + row.words, 0),
    paragraphs: rows.reduce((sum, row) => sum + row.paragraphs, 0),
  };
}
